import React from 'react';
import { Avatar, Badge, Box, Tooltip, Typography } from '@mui/material';
import { IconBuilding } from '@tabler/icons-react';

export interface CustomerHeaderProps {
  name: string;
  dataverseId: string;
  repairShoprId: number;
  /** 'Business' shows a building avatar with a tier mark; 'Residential' shows plain initials. */
  customerType?: 'Business' | 'Residential' | string;
  /** Raw designation (e.g. 'Core', 'Core+', 'CorePlus'); undefined = Entry tier. */
  designation?: string | null;
}

interface BusinessTier {
  letter: string;
  label: string;
}

/** Uniform mark colour for every designation (per design: the same blue as Core). */
const TIER_COLOUR = '#4A90D9';

/** Map a business customer's designation to its avatar tier mark. Returns null
 *  when no designation is set — the building icon then stands alone. */
function getBusinessTier(designation?: string | null): BusinessTier | null {
  const d = (designation ?? '').toLowerCase().replace(/[\s_]/g, '');
  if (d === 'coreplus' || d === 'core+') return { letter: '+', label: 'Core+' };
  if (d === 'core') return { letter: 'C', label: 'Core' };
  if (d === 'entry') return { letter: 'E', label: 'Entry' };
  return null;
}

export const CustomerHeader: React.FC<CustomerHeaderProps> = ({
  name,
  dataverseId,
  repairShoprId,
  customerType,
  designation,
}) => {
  const tooltipId = `customer-header-tooltip-${dataverseId || 'unknown'}`;
  const isBusiness = customerType === 'Business';
  const tier = isBusiness ? getBusinessTier(designation) : null;
  const segmentLabel = isBusiness
    ? (tier ? `Business · ${tier.label}` : 'Business')
    : 'Residential';

  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '—';

  // Residential = plain initials on a quiet neutral. Business = building icon
  // on the same neutral, with a tier-coloured corner badge (+ / C / E).
  const baseAvatar = (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: '#EEF2F7',
        color: '#5A6B7B',
        fontWeight: 700,
        fontSize: '0.78rem',
        letterSpacing: '0.02em',
        flexShrink: 0,
      }}
    >
      {isBusiness ? <IconBuilding size={18} stroke={1.5} /> : initials}
    </Avatar>
  );

  // Tier badge only when a business customer has a designation; otherwise the
  // building icon (or residential initials) stands alone.
  const avatar = tier ? (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={tier.letter}
      sx={{
        flexShrink: 0,
        '& .MuiBadge-badge': {
          minWidth: 15,
          height: 15,
          padding: 0,
          borderRadius: '50%',
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          bgcolor: TIER_COLOUR,
          color: '#ffffff',
          border: '1.5px solid #ffffff',
        },
      }}
    >
      {baseAvatar}
    </Badge>
  ) : (
    baseAvatar
  );

  return (
    <Tooltip
      describeChild
      slotProps={{
        tooltip: {
          sx: {
            userSelect: 'text',
            pointerEvents: 'auto',
          },
        },
      }}
      title={
        <Box>
          <Typography variant="caption" display="block">
            {segmentLabel}
          </Typography>
          <Typography variant="caption" display="block">
            RepairShopr ID: {repairShoprId}
          </Typography>
          <Typography variant="caption" display="block">
            Dataverse ID: {dataverseId}
          </Typography>
        </Box>
      }
    >
      <Box
        id={tooltipId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          cursor: 'text',
          userSelect: 'text',
          borderRadius: 1,
          '&:focus-visible': {
            outline: '2px solid #2563EB',
            outlineOffset: 2,
          },
        }}
        tabIndex={0}
        aria-label={`Selected customer ${name}, ${segmentLabel}. Hover or focus for customer identifiers.`}
      >
        {avatar}
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
      </Box>
    </Tooltip>
  );
};
